/**
 * assetTags — the rules for an asset's TAGS (ENG-1132).
 *
 * A flat array of names on any asset the content browser shows: `["furniture"]`,
 * `["walls"]`. Unreal's shape, deliberately — `AActor::Tags` is a
 * `TArray<FName>` with an `ActorHasTag` lookup (Actor.h:1311), classification
 * rather than key/value, and anyone who has used the editor already knows what
 * a tag does.
 *
 * What they are FOR: an environment uploaded as several splats can say which
 * splat is the furniture and which is the walls, so a character can be told to
 * walk to the sofa (ENG-1132). They are not a replacement for the environment
 * anchors — a tag says what a thing IS, an anchor says where it is.
 *
 * ── The name rule is a MIRROR ───────────────────────────────────────────────
 * TAG_RE is copied from the asset manager's `tagNameRe`
 * (app/api/handlers/asset_tags.go). The two repos cannot import each other, so
 * a drift here means the UI accepts a tag, the PATCH answers 400, and the only
 * evidence is a save that failed for a tag that looked fine. Change one, change
 * both. Same for MAX_TAGS against MaxTagsPerAsset.
 *
 * Pure: no React, no network, no DOM — it runs under `node --test`.
 */

/** Mirrors avatar-asset-manager's tagNameRe. See the header before editing. */
export const TAG_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/

/** Mirrors avatar-asset-manager's MaxTagsPerAsset. */
export const MAX_TAGS = 32

/**
 * One tag in its stored spelling, or null when it is not a usable tag.
 *
 * Lowercasing here rather than comparing case-insensitively later is the same
 * call the server makes: two cards reading "Furniture" and "furniture" look
 * like two tags, and a filter that quietly matches both is harder to trust
 * than one that never let the pair exist.
 */
export function normalizeTag(tag) {
  if (typeof tag !== 'string') return null
  const t = tag.trim().toLowerCase()
  return TAG_RE.test(t) ? t : null
}

/**
 * Why `tag` cannot be used, or null when it can. `existing` is the asset's
 * current tags, so a duplicate is reported as one rather than silently dropped.
 */
export function tagError(tag, existing = []) {
  if (typeof tag !== 'string' || tag.trim() === '') return 'tag is required'
  const t = tag.trim().toLowerCase()
  if (!TAG_RE.test(t)) {
    return 'lowercase letters, digits, _ and - only, max 32'
  }
  if (existing.includes(t)) return `already tagged "${t}"`
  if (existing.length >= MAX_TAGS) return `at most ${MAX_TAGS} tags`
  return null
}

/**
 * Read an asset's tags, in any shape the row reaches the UI in.
 *
 * ALWAYS an array. A row written before the tags migration has no field at all,
 * and a caller that had to write `tags ?? []` would eventually forget — which
 * is a crash on `.map`, not a missing badge. The server resolves the same
 * absence on its side (tagsOrEmpty); this is the client half of that rule.
 *
 * Unreadable entries are dropped rather than rendered, because the server
 * validates everything it stores: the only way to get one is a hand-edited row.
 */
export function readTags(asset) {
  let raw = asset?.tags ?? asset?.raw?.tags ?? null
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw) } catch { raw = null }
  }
  if (!Array.isArray(raw)) return []
  const out = []
  for (const t of raw) {
    const n = normalizeTag(t)
    if (n && !out.includes(n)) out.push(n)
  }
  return out.sort()
}

/**
 * `tags` with `tag` added, in the stored form. Returns the SAME array when the
 * tag is already there, so a caller can compare by identity to see whether
 * anything changed.
 */
export function withTag(tags, tag) {
  const t = normalizeTag(tag)
  if (!t) throw new Error(`invalid tag: ${tagError(tag, tags)}`)
  if (tags.includes(t)) return tags
  if (tags.length >= MAX_TAGS) throw new Error(`at most ${MAX_TAGS} tags per asset`)
  return [...tags, t].sort()
}

/** `tags` without `tag`. The same array when it was not there. */
export function withoutTag(tags, tag) {
  const t = normalizeTag(tag)
  if (!t || !tags.includes(t)) return tags
  return tags.filter(x => x !== t)
}

/** Value equality over two tag lists, for dirty tracking. */
export function tagsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((t, i) => t === b[i])
}

/**
 * Every distinct tag across a list of assets, sorted — the vocabulary, for a
 * filter bar and for suggesting an existing tag rather than a near-miss
 * ("sofas" beside "sofa" is how a tag set stops being useful).
 */
export function allTags(assets) {
  const seen = new Set()
  for (const a of assets ?? []) for (const t of readTags(a)) seen.add(t)
  return [...seen].sort()
}

/** Assets carrying EVERY tag in `tags` (AND, like Unreal's per-tag checks). */
export function filterByTags(assets, tags) {
  const want = (tags ?? []).map(normalizeTag).filter(Boolean)
  if (!want.length) return assets ?? []
  return (assets ?? []).filter(a => {
    const have = readTags(a)
    return want.every(t => have.includes(t))
  })
}

// kind → the CustomAssetManager call that PATCHes it, for the tags body.
//
// A table with a throwing lookup, not an if/else chain, and its own list rather
// than assetFolderPath's: that one is MISSING `garment` (garments have a
// server folder_path and no way to be moved), and inheriting the gap would
// leave garments untaggable for the same invisible reason. Kept beside the
// coverage test so a new taggable kind fails loudly instead of silently doing
// nothing — the folder_path lesson, which this repo has already paid for once.
const TAG_UPDATERS = {
  material:        (cam, id, body) => cam.updateMaterial(id, body),
  texture:         (cam, id, body) => cam.updateTextureAsset(id, body),
  body_anim:       (cam, id, body) => cam.updateAnimationAsset(id, body),
  face_anim:       (cam, id, body) => cam.updateAnimationAsset(id, body),
  audio:           (cam, id, body) => cam.updateAudioAsset(id, body),
  prefab:          (cam, id, body) => cam.updatePrefab(id, body),
  environment:     (cam, id, body) => cam.updateEnvironmentAsset(id, body),
  blueprint_asset: (cam, id, body) => cam.updateBlueprintAsset(id, body),
  garment:         (cam, id, body) => cam.updateGarmentAsset(id, body),
}

/**
 * Kinds that can carry server-side tags.
 *
 * `rig` and `ogs` are absent deliberately: both are cards the registry
 * SYNTHESIZES rather than server rows the browser can PATCH (there is no
 * CustomAssetManager updater for either), so a tag on one would have nowhere
 * to be stored. The asset manager does carry the column on texavatars_assets,
 * so adding `ogs` here later is a client-side change only.
 */
export function taggableKinds() {
  return Object.keys(TAG_UPDATERS).sort()
}

/** True when this asset's kind has somewhere to store a tag. */
export function isTaggable(kind) {
  return Object.prototype.hasOwnProperty.call(TAG_UPDATERS, kind)
}

/**
 * The PATCH call for a kind. Throws rather than returning undefined: a caller
 * that silently skipped an unknown kind would report a saved tag that was never
 * written, which is exactly the failure this table exists to prevent.
 */
export function tagUpdaterFor(kind) {
  const fn = TAG_UPDATERS[kind]
  if (!fn) throw new Error(`assetTags: no tag updater for asset kind "${String(kind)}"`)
  return fn
}

/**
 * Did the write land? `row` is the server's echo of what it stored.
 *
 * A 200 is not proof — the folder_path incident was a PATCH that answered 200
 * with the field bound to nothing, and the client deleted its only record of
 * the change on the strength of that 200. So the echo is compared, and a row
 * that reports no tags at all is `not-reported` rather than success.
 */
export function verifyTagWrite(requested, row) {
  const reported = row?.tags ?? row?.raw?.tags ?? null
  if (reported == null) {
    return { ok: false, reason: 'not-reported', reported: null }
  }
  const got = readTags({ tags: reported })
  const want = [...requested].sort()
  return tagsEqual(got, want)
    ? { ok: true, reported: got }
    : { ok: false, reason: 'mismatch', reported: got }
}
